import React, { useState, useEffect, ReactElement } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Authentication } from '@az/auth-ping';
import { useTranslation } from 'react-i18next';

import { useIdleTimer } from 'react-idle-timer';
import ToastContainer from './layout/ToastContainer';
import Spinner from './layout/Spinner';
import OrderList from './pages/OrderList';
import PickTourFlow from './pages/PickTourFlow';
import PickTourWithError from './pages/PickTourWithError';
import Switch, { Page } from './layout/Switch';
import FullScreenPopup from './layout/FullScreenPopup';
import Main from './layout/Main';

import { createToast, removeToastsOnInactivity } from '../redux/actions/toasts.actions';
import {
  setPickerStore,
  getProximityStore,
  getEmployeeMetadata,
  storeDetectionError,
} from '../redux/actions/pickerStore.actions';
import { setConfigAuthValues } from '../redux/actions/config.actions';
import { sendLog } from '../redux/actions/log.actions';
import {
  getAppPageNameFromState,
  getConfigAuthValuesFromState,
  selectPickerStore,
  selectInactivityTimeoutForToasts,
} from '../redux/selectors';

import {
  APP_PAGE_ORDER_LIST,
  APP_PAGE_PICK_TOUR,
  APP_PAGE_PICK_TOUR_WITH_ERROR,
  USER_LOGIN_MSG,
  ORDERS_OPENED,
  ORDERS_REFRESH,
} from '../constants';

import { determineDeviceType, getAppVersion, getSystemDetails, padTo8LeadingZeroes } from '../utils';

import { AppLogType, ConfigAuthValues } from '../types';

import '../styles/main.css';
import styles from './App.css';

import { setEmployeeData } from '../redux/actions/employeeData.actions';
import useRecoverLastSessionDetails from '../hooks/useRecoverLastSessionDetails';
import { updateSSOSessions } from '../userSSOSessions';
import { setLogDispatch } from '../services/whispersSsoLoggingService';
import { loadWhatfixScript, cleanupWhatfixScript } from '../utils/whatfixLoader';

const App = (): ReactElement => {
  const [hasConfigError, setHasConfigError] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isWaitingForLocationPrompt, setIsWaitingForLocationPrompt] = useState(false);
  const [hasUpdatedSSO, setHasUpdatedSSO] = useState(() => localStorage.getItem('updateSSOCalled') === 'true');

  const { t } = useTranslation();

  const currentPageName = useSelector(getAppPageNameFromState);
  const configAuthValues = useSelector(getConfigAuthValuesFromState);
  const pickerStore = useSelector(selectPickerStore);
  const inactivityTimeoutForToasts = useSelector(selectInactivityTimeoutForToasts);
  const deviceType = determineDeviceType();

  const devices = configAuthValues?.SSO_DEVICES || [];
  const isMobile = devices.indexOf(deviceType.toUpperCase()) !== -1;

  const dispatch = useDispatch();

  const { getSessionRecoveryData, clearSessionRecoveryData } = useRecoverLastSessionDetails(isAuthenticated);

  const handleOnActive = () => {
    dispatch(removeToastsOnInactivity());
  };

  useIdleTimer({
    timeout: inactivityTimeoutForToasts,
    onActive: handleOnActive,
  });

  useEffect(() => {
    setLogDispatch(dispatch);
  }, [dispatch]);

  // Load Whatfix script when configuration is available and enabled
  useEffect(() => {
    if (configAuthValues?.WHATFIX_ENABLED && configAuthValues?.WHATFIX_SCRIPT_URL) {
      loadWhatfixScript(configAuthValues.WHATFIX_SCRIPT_URL, configAuthValues.WHATFIX_TIMEOUT);
    }

    // Cleanup on unmount
    return () => {
      if (configAuthValues?.WHATFIX_ENABLED) {
        cleanupWhatfixScript();
      }
    };
  }, [configAuthValues]);

  useEffect(() => {
    sessionStorage.removeItem(ORDERS_OPENED);
    sessionStorage.removeItem(ORDERS_REFRESH);
    const isLocalDev = JSON.parse(process.env.IS_LOCAL_DEV || 'false');
    const setRefreshData = (configModule: ConfigAuthValues) => {
      sessionStorage.setItem(
        'refreshData',
        JSON.stringify({
          AUTH_ROOT: configModule.AUTH_ROOT,
          REDIRECT_URI: configModule.REDIRECT_URI,
          CLIENT_ID: configModule.CLIENT_ID,
          SSO_DEVICES: configModule.SSO_DEVICES,
        })
      );
    };
    if (isLocalDev) {
      import('../../config.local.js').then(configModule => {
        const config = configModule.default as ConfigAuthValues;
        dispatch(setConfigAuthValues({ values: config }));
        setRefreshData(config);
      });
    } else {
      fetch('/config.json')
        .then(response => response.json())
        .then(json => {
          dispatch(setConfigAuthValues({ values: json }));
          setRefreshData(json);
        })
        .catch(() => setHasConfigError(true));
    }
  }, [dispatch]);

  const ConfigDisplay = () => (
    <Main>
      {hasConfigError ? (
        <>
          <h1 className={styles.message}>Configuration failed to load</h1>
          <h1 className={styles.message}>Please refresh the page</h1>
        </>
      ) : (
        <h1 className={styles.message}>Loading configuration...</h1>
      )}
    </Main>
  );

  return (
    <>
      <Spinner
        isInitialSetup={(!configAuthValues || !isAuthenticated || isWaitingForLocationPrompt) && !hasConfigError}
      />

      {configAuthValues?.AUTH_ROOT ? (
        <Authentication
          baseUrl={configAuthValues.AUTH_ROOT}
          publicClientId={configAuthValues.CLIENT_ID}
          redirectUri={configAuthValues.REDIRECT_URI}
          scope={configAuthValues.AUTH_SCOPE}
          skipAuthorization
          getUserData
        >
          {({ authenticated, disabledForDevelopment, userData }) => {
            if (!isAuthenticated && (authenticated || disabledForDevelopment)) {
              const initializeSSOUpdateSession = async () => {
                setIsAuthenticated(true);
                if (isMobile) {
                  if (configAuthValues?.APIGEE_BASE_URL) {
                    sessionStorage.setItem('APIGEE_BASE_URL', configAuthValues.APIGEE_BASE_URL);
                  }
                  if (!hasUpdatedSSO) {
                    try {
                      await updateSSOSessions();
                      setHasUpdatedSSO(true);
                      localStorage.setItem('updateSSOCalled', 'true');
                    } catch (error) {
                      console.log('Error updating SSO sessions:', error);
                    }
                  }
                }
                dispatch(
                  setEmployeeData({
                    ignitionID: userData.sub,
                    name: userData.cn,
                    firstName: userData.givenName || null,
                    lastName: userData.sn || null,
                  })
                );

                if (isMobile) {
                  setIsWaitingForLocationPrompt(true);
                }
                if (!isMobile) {
                  /**
                   * Supply Chain expects store number to be *exactly* 8 digits, so store numbers
                   * shorter than that need to be left-padded.  As additional safety checks, make sure
                   * we got a string to begin with and store numbers longer than 8 should have left
                   * padding removed down to length 8.
                   */
                  let storeNumber: string | null | undefined = userData.departmentNumber;
                  if (storeNumber === undefined || storeNumber === null) {
                    storeNumber = '00000000';
                  } else if (storeNumber.length < 8) {
                    storeNumber = padTo8LeadingZeroes(storeNumber);
                  } else if (storeNumber.length > 8) {
                    storeNumber = storeNumber.slice(-8);
                  }
                  dispatch(setPickerStore({ storeID: storeNumber }));
                }

                if (!sessionStorage.getItem('userLoginLogged')) {
                  const sessionRecovery = getSessionRecoveryData();
                  let appVersion = 0;
                  try {
                    appVersion = await getAppVersion().catch(() => 0);
                  } finally {
                    dispatch(
                      sendLog({
                        logEvent: AppLogType.USER_LOGIN,
                        logMessage: USER_LOGIN_MSG,
                        logDetails: {
                          uid: userData.sub,
                          loginTimestamp: new Date().toISOString(),
                          ...getSystemDetails(),
                          appVersion,
                          storeID: isMobile ? null : padTo8LeadingZeroes(userData.departmentNumber || '00000000'),
                          isDetectingStore: isMobile,
                          shouldPromptStoreSelection: false,
                          shouldPromptStoreVerification: false,
                          storeToVerify: null,
                          sessionRecovered: !!sessionRecovery,
                          previousLogin: sessionRecovery?.login || null,
                          previousLogout: sessionRecovery?.logout || null,
                        },
                      })
                    );
                    // Clear session recovery data after logging
                    if (sessionRecovery) {
                      clearSessionRecoveryData();
                    }
                    sessionStorage.setItem('userLoginLogged', 'true');
                  }
                }
              };
              initializeSSOUpdateSession();
            } else if (
              isAuthenticated &&
              !pickerStore.storeID &&
              !pickerStore.isDetectingStore &&
              !pickerStore.shouldPromptStoreSelection &&
              isWaitingForLocationPrompt
            ) {
              const getCurrentPositionSuccessCallback: PositionCallback = position => {
                setIsWaitingForLocationPrompt(false);
                if (userData) {
                  dispatch(
                    getProximityStore({
                      latitude: position.coords.latitude,
                      longitude: position.coords.longitude,
                      employeeID: userData.sub,
                    })
                  );
                } else {
                  dispatch(
                    getProximityStore({
                      latitude: position.coords.latitude,
                      longitude: position.coords.longitude,
                    })
                  );
                }
              };
              const getCurrentPositionErrorCallback: PositionErrorCallback = error => {
                setIsWaitingForLocationPrompt(false);
                if (userData) {
                  dispatch(
                    getEmployeeMetadata({
                      employeeID: userData.sub,
                      positionError: error.code,
                    })
                  );
                } else {
                  dispatch(storeDetectionError());
                  if (error.code === 1) {
                    dispatch(
                      createToast({
                        type: 'error',
                        message: t(`ERRORS.ENABLE_LOCATION`),
                      })
                    );
                  }
                }
              };
              window.navigator.geolocation.getCurrentPosition(
                getCurrentPositionSuccessCallback,
                getCurrentPositionErrorCallback,
                {
                  timeout: 5000,
                }
              );
            }

            return isAuthenticated && !isWaitingForLocationPrompt ? (
              <>
                <FullScreenPopup />
                <ToastContainer />
                <Switch currentPageName={currentPageName}>
                  <Page name={APP_PAGE_ORDER_LIST}>
                    <OrderList />
                  </Page>
                  <Page name={APP_PAGE_PICK_TOUR}>
                    <PickTourFlow />
                  </Page>
                  <Page name={APP_PAGE_PICK_TOUR_WITH_ERROR}>
                    <PickTourWithError />
                  </Page>
                </Switch>
              </>
            ) : (
              <Main>
                <h1 className={styles.message}>Authenticating...</h1>
              </Main>
            );
          }}
        </Authentication>
      ) : (
        <ConfigDisplay />
      )}
    </>
  );
};

export default App;
